import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, TextField, InputAdornment, MenuItem, FormControl, InputLabel, Select, TablePagination, Snackbar, Alert, Tooltip, CircularProgress, Avatar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import HotelFormModal from '../../components/HotelFormModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ChangeHotelStatusModal from '../../components/ChangeHotelStatusModal';
import ArchiveHotelModal from '../../components/ArchiveHotelModal';
import RestoreHotelModal from '../../components/RestoreHotelModal';
import ManageHotelAdminsModal from '../../components/ManageHotelAdminsModal';
import HotelAuditLogsModal from '../../components/HotelAuditLogsModal';
import CloneHotelSettingsModal from '../../components/CloneHotelSettingsModal';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { HotelService } from '../../api/services/hotelService';
import ExportFormatModal from '../../components/ExportFormatModal';
import ReplayIcon from '@mui/icons-material/Replay';
import { setActiveCurrency } from '../../utils/currency';
import { standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';

export default function Hotels() {
    const hotelsList = useSelector(state => state.hotels.list) || [];
    const totalCount = useSelector(state => state.hotels.total);
    const isLoading = useSelector(state => state.hotels.isLoading);
    const storeError = useSelector(state => state.hotels.error);
    const dispatch = useDispatch();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingHotel, setEditingHotel] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, hotelId: null });
    const [statusDialog, setStatusDialog] = useState({ open: false, hotel: null });
    const [archiveDialog, setArchiveDialog] = useState({ open: false, hotel: null });
    const [restoreDialog, setRestoreDialog] = useState({ open: false, hotel: null });
    const [manageAdminsDialog, setManageAdminsDialog] = useState({ open: false, hotelId: null });
    const [auditLogsDialog, setAuditLogsDialog] = useState({ open: false, hotelId: null });
    const [cloneDialog, setCloneDialog] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [isExporting, setIsExporting] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Pagination States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    useEffect(() => {
        if (storeError) {
            setNotification({ open: true, message: storeError, severity: 'error' });
            dispatch.hotels.setError(null); // Clear error after showing
        }
    }, [storeError, dispatch]);

    const refreshHotels = useCallback(() => {
        return dispatch.hotels.fetchHotelsAsync({
            search: searchTerm,
            page: page,
            limit: rowsPerPage,
            status: statusFilter
        });
    }, [dispatch, searchTerm, page, rowsPerPage, statusFilter]);

    useEffect(() => {
        refreshHotels();
    }, [refreshHotels]);

    const filteredHotels = hotelsList;

    // Reset pagination when filters change
    useEffect(() => {
        setPage(0);
    }, [searchTerm, statusFilter]);

    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('All');
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenAdd = () => {
        setEditingHotel(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (hotel) => {
        setEditingHotel(hotel);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingHotel(null);
    };

    const handleSaveHotel = async (hotelData) => {
        try {
            console.log('[Hotels] Create/Update payload:', JSON.stringify(hotelData, null, 2));
            if (editingHotel) {
                await dispatch.hotels.saveHotelAsync(hotelData);
                setNotification({ open: true, message: 'Hotel updated successfully!', severity: 'success' });
            } else {
                await dispatch.hotels.saveHotelAsync(hotelData);
                setNotification({
                    open: true,
                    message: 'New hotel property created successfully!',
                    severity: 'success'
                });
                // Refresh tenants dropdown so the new property appears instantly
                try { dispatch.auth.fetchTenantsAsync(); } catch (_) { /* non-critical */ }
            }
            if (hotelData?.currency) {
                setActiveCurrency(hotelData.currency);
            }
            await refreshHotels();
        } catch (error) {
            console.error('[Hotels] Save failed:', error);
            setNotification({ open: true, message: error.message || 'Failed to save hotel', severity: 'error' });
            throw error; // Re-throw so HotelFormModal can show inline error too
        }
    };

    const handleStatusClick = (hotel) => {
        setStatusDialog({ open: true, hotel });
    };

    const handleSaveStatus = async ({ id, tenantId, status, reason }) => {
        try {
            await dispatch.hotels.changeHotelStatusAsync({ id, tenantId, status, reason });
            setNotification({ open: true, message: 'Hotel status updated successfully!', severity: 'success' });
            // Refresh tenants dropdown in case status changes affect switcher visibility
            try { dispatch.auth.fetchTenantsAsync(); } catch (_) {}
            await refreshHotels();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update hotel status', severity: 'error' });
            throw error;
        }
    };

    const closeStatusDialog = () => {
        setStatusDialog({ open: false, hotel: null });
    };

    const handleArchiveClick = (hotel) => {
        setArchiveDialog({ open: true, hotel });
    };

    const handleSaveArchive = async ({ id, tenantId, reason, notes }) => {
        try {
            await dispatch.hotels.archiveHotelAsync({ id, tenantId, reason, notes });
            setNotification({ open: true, message: 'Hotel archived successfully!', severity: 'success' });
            // Refresh tenants dropdown in case archive affects switcher visibility
            try { dispatch.auth.fetchTenantsAsync(); } catch (_) {}
            await refreshHotels();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to archive hotel', severity: 'error' });
            throw error;
        }
    };

    const closeArchiveDialog = () => {
        setArchiveDialog({ open: false, hotel: null });
    };

    const handleRestoreClick = (hotel) => {
        setRestoreDialog({ open: true, hotel });
    };

    const handleSaveRestore = async ({ id, tenantId }) => {
        try {
            await dispatch.hotels.restoreHotelAsync({ id, tenantId });
            setNotification({ open: true, message: 'Hotel restored successfully!', severity: 'success' });
            try { dispatch.auth.fetchTenantsAsync(); } catch (_) {}
            await refreshHotels();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to restore hotel', severity: 'error' });
            throw error;
        }
    };

    const closeRestoreDialog = () => {
        setRestoreDialog({ open: false, hotel: null });
    };

    const handleDeleteClick = (id) => {
        setDeleteDialog({ open: true, hotelId: id });
    };

    const confirmDelete = async () => {
        if (deleteDialog.hotelId) {
            try {
                await dispatch.hotels.deleteHotelAsync(deleteDialog.hotelId);
                setNotification({ open: true, message: 'Hotel deleted successfully!', severity: 'success' });
                await refreshHotels();
            } catch (error) {
                setNotification({ open: true, message: error.message || 'Failed to delete hotel', severity: 'error' });
            }
        }
        setDeleteDialog({ open: false, hotelId: null });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, hotelId: null });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleExport = async (format) => {
        setExportModalOpen(false);
        setIsExporting(true);
        try {
            const tenantId = JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1';
            const response = await HotelService.exportHotels(tenantId, format);
            
            const mimeTypes = {
                'csv': 'text/csv',
                'excel': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'pdf': 'application/pdf'
            };
            
            const blob = new Blob([response.data], { 
                type: (response.headers && response.headers['content-type']) || mimeTypes[format] || 'application/octet-stream' 
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const ext = format === 'excel' ? 'xlsx' : format;
            let filename = `hotels-export-${new Date().toISOString().split('T')[0]}.${ext}`;
            
            const contentDisposition = response.headers && response.headers['content-disposition'];
            if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) { 
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setNotification({ open: true, message: 'Hotels list export compiled and downloaded successfully', severity: 'success' });
        } catch (error) {
            console.error('Export error', error);
            let errorMessage = 'Failed to export hotels list';
            if (error.response && error.response.data && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json.message || errorMessage;
                } catch (e) {
                    // fallback
                }
            } else {
                errorMessage = error.message || errorMessage;
            }
            setNotification({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Hotels Management</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setCloneDialog(true)}
                        sx={standardSecondaryButtonSx}
                    >
                        Clone Settings
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setExportModalOpen(true)} 
                        disabled={isExporting}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Hotels'}
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={isExporting}
                        title="Export Hotels"
                    />
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                        Add Hotel
                    </Button>
                </Box>
            </Box>

            {/* Filters Section */}
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search name or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={statusFilter}
                            displayEmpty
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="INACTIVE">Inactive</MenuItem>
                            <MenuItem value="SUSPENDED">Suspended</MenuItem>
                            <MenuItem value="ARCHIVED">Archived</MenuItem>
                        </Select>
                    </FormControl>

                    {(searchTerm !== '' || statusFilter !== 'All') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            onClick={handleResetFilters}
                            sx={standardResetButtonSx}
                        >
                            Reset
                        </Button>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Hotel Name</TableCell>
                            <TableCell>Tenant ID</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Rooms</TableCell>
                            <TableCell>Occupancy</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredHotels?.length > 0 ? (
                            filteredHotels?.map((hotel) => (
                                <TableRow key={hotel.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={hotel.logoUrl} 
                                                alt={hotel.name} 
                                                variant="rounded" 
                                                sx={{ width: 40, height: 40, bgcolor: 'primary.light', border: '1px solid rgba(0,0,0,0.1)' }}
                                            >
                                                {hotel.name ? hotel.name.charAt(0).toUpperCase() : 'H'}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight="bold">
                                                {hotel.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{hotel.tenantId || 'N/A'}</TableCell>
                                    <TableCell>{hotel.location || 'N/A'}</TableCell>
                                    <TableCell>{hotel.hotelType || 'N/A'}</TableCell>
                                    <TableCell>{hotel.totalRooms ?? 'N/A'}</TableCell>
                                    <TableCell>{hotel.stats?.todayOccupancy || '0%'}</TableCell>
                                    <TableCell>⭐ {hotel.rating ?? 'N/A'}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const status = String(hotel.status || 'ACTIVE').toUpperCase();
                                            let color = 'default';
                                            if (status === 'ACTIVE') color = 'success';
                                            else if (status === 'INACTIVE') color = 'error';
                                            else if (status === 'SETUP') color = 'info';
                                            else if (status === 'SUSPENDED') color = 'warning';
                                            return (
                                                <Chip
                                                    label={hotel.status || 'ACTIVE'}
                                                    size="small"
                                                    color={color}
                                                    onClick={() => handleStatusClick(hotel)}
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        cursor: 'pointer', 
                                                        transition: 'transform 0.2s, opacity 0.2s',
                                                        '&:hover': { 
                                                            opacity: 0.85,
                                                            transform: 'scale(1.05)'
                                                        } 
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Change Status" arrow>
                                            <IconButton color="warning" onClick={() => handleStatusClick(hotel)}><ToggleOnIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Manage Admins" arrow>
                                            <IconButton color="secondary" onClick={() => setManageAdminsDialog({ open: true, hotelId: hotel.id })}><AdminPanelSettingsIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Activity History" arrow>
                                            <IconButton color="info" onClick={() => setAuditLogsDialog({ open: true, hotelId: hotel.id })}><HistoryIcon /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Hotel" arrow>
                                            <IconButton color="primary" onClick={() => handleOpenEdit(hotel)}><EditIcon /></IconButton>
                                        </Tooltip>
                                        {String(hotel.status || '').toUpperCase() !== 'ARCHIVED' && (
                                            <Tooltip title="Archive Hotel" arrow>
                                                <IconButton color="default" onClick={() => handleArchiveClick(hotel)}><ArchiveIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                        {String(hotel.status || '').toUpperCase() === 'ARCHIVED' && (
                                            <Tooltip title="Restore Hotel" arrow>
                                                <IconButton color="info" onClick={() => handleRestoreClick(hotel)}><RestoreIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Delete Hotel" arrow>
                                            <IconButton color="error" onClick={() => handleDeleteClick(hotel.id)}><DeleteIcon /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No hotels found matching your filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    component="div"
                    count={filteredHotels?.length === 0 ? 0 : (totalCount || filteredHotels?.length || 0)}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            <HotelFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveHotel}
                editingHotel={editingHotel}
            />

            <ConfirmDeleteModal
                open={deleteDialog.open}
                onClose={closeDeleteDialog}
                onConfirm={confirmDelete}
                itemName="hotel"
            />

            <ChangeHotelStatusModal
                open={statusDialog.open}
                onClose={closeStatusDialog}
                onSave={handleSaveStatus}
                hotel={statusDialog.hotel}
            />

            <ArchiveHotelModal
                open={archiveDialog.open}
                onClose={closeArchiveDialog}
                onSave={handleSaveArchive}
                hotel={archiveDialog.hotel}
            />

            <RestoreHotelModal
                open={restoreDialog.open}
                onClose={closeRestoreDialog}
                onSave={handleSaveRestore}
                hotel={restoreDialog.hotel}
            />

            <ManageHotelAdminsModal
                open={manageAdminsDialog.open}
                onClose={() => setManageAdminsDialog({ open: false, hotelId: null })}
                hotelId={manageAdminsDialog.hotelId}
                setNotification={setNotification}
            />

            <HotelAuditLogsModal
                open={auditLogsDialog.open}
                onClose={() => setAuditLogsDialog({ open: false, hotelId: null })}
                tenantId={auditLogsDialog.hotelId}
                setNotification={setNotification}
            />

            <CloneHotelSettingsModal 
                open={cloneDialog}
                onClose={() => setCloneDialog(false)}
                setNotification={setNotification}
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
